import { InputField } from "./InputField";
import { useAppDispatch, useAppSelector } from "@/store/app/hooks";
import { loadDataSelector } from "@/store/features/load-info/loadInfoSelectors";
import {
  tripDataLoadingSelector,
  tripDataSelector,
} from "@/store/features/trip-info/tripInfoSelectors";
import { Loader } from "../loader";
import { sendAmplitudeData } from "@/analytics";
import { AMPLITUDE_EVENTS } from "@/analytics/events";
import { Switch } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  setActiveUnitSystem,
  setFuelPrice,
  setLoading,
} from "@/store/features/trip-info/tripInfoSlice";
import { getFuelDataByOrigin, updatePricePrecision } from "@/modules/server";

export const CalculatorView = () => {
  const dispatch = useAppDispatch();
  const loadInfo = useAppSelector(loadDataSelector);
  const tripInfo = useAppSelector(tripDataSelector);
  const loading = useAppSelector(tripDataLoadingSelector);

  const {
    distance,
    tollCost,
    fuelPrice,
    activeUnitSystem,
    isUnitsSwitchAccessible,
  } = tripInfo || {};

  const [toll, setToll] = useState<string>("0");
  const [miles, setMiles] = useState<string>("0");
  const [rpm, setRpm] = useState<string>("0");
  const [consumption, setConsumption] = useState<string>("6.8");
  const [rate, setRate] = useState<string>("0");
  const [fuelCost, setFuelCost] = useState<string>("0");
  const [spendings, setSpendings] = useState<string>("0");

  const gallonRef = useRef(fuelPrice?.gallon);
  gallonRef.current = fuelPrice?.gallon;
  const milesRef = useRef(miles);
  const rateRef = useRef(rate);
  milesRef.current = miles;
  rateRef.current = rate;
  const edited = useRef({ rate: false, miles: false, fuel: false });

  const isMetric = useMemo(
    () => activeUnitSystem === "metric",
    [activeUnitSystem]
  );

  const calculateFuelCost = useCallback(
    (miles: number, consumption: number) => {
      if (consumption === 0 || miles === 0) return "0";
      const fuel = isMetric ? +fuelPrice.liter : +fuelPrice.gallon;

      const fuelCost = isMetric
        ? (miles / 100) * consumption * fuel
        : (miles / consumption) * fuel;

      return fuelCost.toFixed(2) || "0";
    },
    [fuelPrice.gallon, fuelPrice.liter, isMetric]
  );

  useEffect(() => {
    edited.current = { rate: false, miles: false, fuel: false };
  }, [loadInfo?.refId, loadInfo?.origin, loadInfo?.destination]);

  useEffect(() => {
    const consumptionMetric = +(
      localStorage.getItem("consumptionMetric") || "35"
    );
    const consumptionImperial = +(
      localStorage.getItem("consumptionImperial") || "6.8"
    );

    if (tripInfo) {
      const consumption = isMetric
        ? consumptionMetric.toString().replaceAll(",", ".")
        : consumptionImperial.toString().replaceAll(",", ".");
      const storeMiles = isMetric ? distance.kilometersRaw : distance.milesRaw;
      const storeToll = isMetric ? +tollCost.cad : +tollCost.usd;
      const storeRate = +(loadInfo?.rate || 0);
      const milesNum = edited.current.miles ? +milesRef.current : +storeMiles;
      const rateNum = edited.current.rate ? +rateRef.current : storeRate;

      setConsumption(consumption.toString());
      setToll(storeToll.toString());
      if (!edited.current.miles) setMiles(storeMiles.toString());
      if (!edited.current.rate) setRate(storeRate.toString());
      if (!edited.current.rate && !edited.current.miles) {
        setRpm(milesNum ? (rateNum / milesNum).toFixed(2) : "0");
      }
      if (!edited.current.fuel) {
        setFuelCost(calculateFuelCost(milesNum, +consumption));
      }
    }
  }, [
    calculateFuelCost,
    distance.kilometersRaw,
    distance.milesRaw,
    isMetric,
    loadInfo?.rate,
    tollCost.cad,
    tollCost.usd,
    tripInfo,
  ]);

  useEffect(() => {
    if (!loadInfo?.origin) return;

    getFuelPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadInfo?.origin]);

  const getFuelPrice = async () => {
    dispatch(setLoading(true));

    getFuelDataByOrigin(loadInfo?.origin || "")
      .then((fuelPrice) => {
        if (+(gallonRef.current || 0)) return;
        const isCanadian = fuelPrice.country?.toLowerCase() === "canada";
        updatePricePrecision(fuelPrice);
        dispatch(
          setFuelPrice({
            gallon: isCanadian
              ? (+fuelPrice.diesel * 3.785).toFixed(2) || "0"
              : (+fuelPrice.diesel).toFixed(2) || "0",
            liter: isCanadian
              ? (+fuelPrice.diesel).toFixed(2) || "0"
              : (+fuelPrice.diesel / 3.785).toFixed(2) || "0",
          })
        );
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const currencySign = useMemo(() => (isMetric ? "C$" : "$"), [isMetric]);

  const consumptionTerm = useMemo(
    () => (isMetric ? "L/100km" : "MPG"),
    [isMetric]
  );

  const rpmTerm = useMemo(() => (isMetric ? "RPK" : "RPM"), [isMetric]);

  const handleUnitChange = (value: boolean) => {
    edited.current.miles = false;
    edited.current.fuel = false;
    dispatch(setActiveUnitSystem(value ? "metric" : "imperial"));
    setConsumption(value ? "35" : "6.8");
  };

  const handleRPMChange = (value: string) => {
    edited.current.rate = true;
    setRpm(value);
    setRate((+miles * +value).toFixed(2));
  };

  const handleRateChange = (value: string) => {
    edited.current.rate = true;
    setRate(value);
    setRpm(+miles ? (+value / +miles).toFixed(2) : "0");
  };

  const handleMilesChange = (value: string) => {
    edited.current.miles = true;
    setMiles(value);
    setRpm(+value ? (+rate / +value).toFixed(2) : "0");
    setFuelCost(calculateFuelCost(+value, +consumption));
  };

  const handleFuelChange = (value: string) => {
    edited.current.fuel = true;
    setFuelCost(value);
  };

  const handleConsumptionChange = (value: string) => {
    if (isMetric) {
      localStorage.setItem("consumptionMetric", value.toString());
    } else {
      localStorage.setItem("consumptionImperial", value.toString());
    }
    setConsumption(value);
    setFuelCost(calculateFuelCost(+miles, +value));
  };

  const handleMilesFocus = () =>
    sendAmplitudeData(AMPLITUDE_EVENTS.popupMilesValueChanged);
  const handleRpmCalculatorFocus = () =>
    sendAmplitudeData(AMPLITUDE_EVENTS.popupRpmCalculatorValueChanged);
  const handleMpgFocus = () =>
    sendAmplitudeData(AMPLITUDE_EVENTS.popupMpgValueChanged);
  const handleRateFocus = () =>
    sendAmplitudeData(AMPLITUDE_EVENTS.popupRateValueChanged);
  const handleFuelCostFocus = () =>
    sendAmplitudeData(AMPLITUDE_EVENTS.popupFuelCostValueChanged);

  if (!tripInfo || !loadInfo) return <div></div>;

  return (
    <div className="relative flex flex-col justify-between bg-brand-blue-700 text-white">
      <div className="h-full flex flex-col gap-2 p-3">
        <div className="flex w-full justify-between">
          <h2 className="font-semibold text-xl">Calculator</h2>
          {isUnitsSwitchAccessible && (
            <div className="flex gap-1 items-center">
              <small>mi</small>
              <Switch
                size="sm"
                isSelected={activeUnitSystem === "metric"}
                onValueChange={handleUnitChange}
                classNames={{
                  wrapper: "mr-0 !bg-brand-700",
                }}
              />
              <small>km</small>
            </div>
          )}
        </div>
        <div className="h-full flex flex-col gap-2">
          <div className="w-full grid grid-cols-3 gap-2">
            <InputField
              label="Trip"
              min={0}
              value={miles.toString()}
              onValueChange={handleMilesChange}
              onFocus={handleMilesFocus}
            />
            <InputField
              label={rpmTerm}
              step={0.01}
              min={0}
              value={rpm.toString()}
              onValueChange={handleRPMChange}
              onFocus={handleRpmCalculatorFocus}
            />
            <InputField
              label={consumptionTerm}
              step={0.01}
              min={0.01}
              value={consumption.toString()}
              onValueChange={handleConsumptionChange}
              onFocus={handleMpgFocus}
            />
            <InputField
              label="Rate"
              min={0}
              step={10}
              value={rate.toString()}
              onValueChange={handleRateChange}
              onFocus={handleRateFocus}
            />
            <InputField
              label="Fuel"
              min={0}
              step={0.01}
              value={fuelCost.toString()}
              onValueChange={handleFuelChange}
              onFocus={handleFuelCostFocus}
            />
            <InputField
              label="Expenses"
              min={0}
              step={0.01}
              value={spendings.toString()}
              onValueChange={setSpendings}
            />
          </div>
        </div>
      </div>
      <div className="h-[50px] shrink-0 px-4 py-5 flex justify-between items-center bg-brand-700">
        <h2 className="font-normal text-md">Profit</h2>
        <p className="font-semibold text-md">
          {currencySign} {(+rate - +fuelCost - +toll - +spendings).toFixed(2)}
        </p>
      </div>
      <Loader dark show={loading} />
    </div>
  );
};
